export interface CanonicalPatientName {
  firstName: string;
  middleName: string;
  lastName: string;
  displayName: string;
  fullName: string;
  patientName: string;
}

const UNKNOWN_PATIENT_RE = /^unknown patient$/i;

export const cleanPatientNameValue = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ');

const firstPresentValue = (...values: unknown[]) =>
  values.map(cleanPatientNameValue).find(Boolean) || '';

const foldNameToken = (value: unknown): string =>
  cleanPatientNameValue(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\p{L}\p{N}]+/gu, '')
    .toLowerCase();

export function patientNameHasRepeatedMiddleToken(
  patientName: unknown,
  middleName: unknown,
) {
  const middleTokens = cleanPatientNameValue(middleName)
    .split(/\s+/)
    .map(foldNameToken)
    .filter(Boolean);
  if (!middleTokens.length) return false;

  const nameTokens = cleanPatientNameValue(patientName)
    .split(/\s+/)
    .map(foldNameToken)
    .filter(Boolean);
  return nameTokens.some(
    (token, index) =>
      index > 0 &&
      token === nameTokens[index - 1] &&
      middleTokens.includes(token),
  );
}

const joinNameParts = (...values: unknown[]) =>
  values.map(cleanPatientNameValue).filter(Boolean).join(' ');

export function parsePatientNameFallback(value: unknown) {
  const raw = cleanPatientNameValue(value);
  if (!raw || UNKNOWN_PATIENT_RE.test(raw)) {
    return {firstName: '', middleName: '', lastName: ''};
  }

  const commaIndex = raw.indexOf(',');
  if (commaIndex >= 0) {
    const lastName = raw.slice(0, commaIndex).trim();
    const givenNameTokens = raw
      .slice(commaIndex + 1)
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    return {
      firstName: givenNameTokens[0] || '',
      middleName: givenNameTokens.slice(1).join(' '),
      lastName,
    };
  }

  const tokens = raw.split(/\s+/).filter(Boolean);
  if (tokens.length === 1) {
    return {firstName: tokens[0], middleName: '', lastName: ''};
  }

  return {
    firstName: tokens[0] || '',
    middleName: tokens.slice(1, -1).join(' '),
    lastName: tokens[tokens.length - 1] || '',
  };
}

function removeDuplicatedMiddleTokenFromLastName(
  middleName: unknown,
  lastName: unknown,
) {
  const cleanMiddleName = cleanPatientNameValue(middleName);
  const cleanLastName = cleanPatientNameValue(lastName);
  if (!cleanMiddleName || !cleanLastName) return cleanLastName;

  const middleTokens = cleanMiddleName.split(/\s+/).map(foldNameToken);
  const remainingLastNameTokens = cleanLastName.split(/\s+/);

  while (remainingLastNameTokens.length > 0) {
    const firstLastNameToken = foldNameToken(remainingLastNameTokens[0]);
    if (!firstLastNameToken || !middleTokens.includes(firstLastNameToken)) {
      break;
    }
    remainingLastNameTokens.shift();
  }

  return remainingLastNameTokens.join(' ');
}

function removeConsecutiveDuplicateNameTokens(value: unknown) {
  const tokens = cleanPatientNameValue(value).split(/\s+/).filter(Boolean);
  const result: string[] = [];
  let previousFoldedToken = '';

  tokens.forEach(token => {
    const foldedToken = foldNameToken(token);
    if (foldedToken && foldedToken === previousFoldedToken) return;
    result.push(token);
    previousFoldedToken = foldedToken;
  });

  return result.join(' ');
}

export function normalizePatientNameParts(
  values: Record<string, unknown> | null | undefined = {},
): CanonicalPatientName {
  const source = values ?? {};
  const fallbackName = firstPresentValue(
    source.patientName,
    source.patient_name,
    source.displayName,
    source.name,
    source.fullName,
    source.full_name,
  );
  const fallbackParts = parsePatientNameFallback(fallbackName);
  const firstName = firstPresentValue(
    source.firstName,
    source.first_name,
    fallbackParts.firstName,
  );
  const middleName = firstPresentValue(
    source.middleName,
    source.middle_name,
    fallbackParts.middleName,
  );
  const dedupedMiddleName = removeConsecutiveDuplicateNameTokens(middleName);
  const lastName = removeDuplicatedMiddleTokenFromLastName(
    dedupedMiddleName,
    firstPresentValue(
      source.lastName,
      source.last_name,
      fallbackParts.lastName,
    ),
  );
  const displayName = joinNameParts(firstName, lastName);
  const fullName =
    joinNameParts(firstName, dedupedMiddleName, lastName) || displayName;
  const patientName = displayName || fullName || fallbackName;

  return {
    firstName,
    middleName: dedupedMiddleName,
    lastName,
    displayName,
    fullName,
    patientName,
  };
}

export function patientDisplayNameFromRecord(
  record: Record<string, any> | null | undefined = {},
): string {
  const name = patientNameFromRecord(record);
  return name.displayName || name.patientName || name.fullName;
}

export function patientNameFromRecord(
  record: Record<string, any> | null | undefined = {},
): CanonicalPatientName {
  const source = record ?? {};
  const localPatient = source.localPatient || {};
  const ehrPatient = source.ehrPatient || {};
  const plannerPatient = source.patient || {};

  const fallbackName = firstPresentValue(
    source.patientName,
    source.patient_name,
    source.displayName,
    source.name,
    source.fullName,
    source.full_name,
    localPatient.patientName,
    localPatient.patient_name,
    localPatient.displayName,
    localPatient.fullName,
    localPatient.full_name,
    ehrPatient.patientName,
    ehrPatient.patient_name,
    ehrPatient.displayName,
    ehrPatient.fullName,
    ehrPatient.full_name,
    plannerPatient.patientName,
    plannerPatient.patient_name,
    plannerPatient.displayName,
    plannerPatient.fullName,
    plannerPatient.full_name,
  );
  const fallbackParts = parsePatientNameFallback(fallbackName);

  return normalizePatientNameParts({
    firstName: firstPresentValue(
      localPatient.first_name,
      localPatient.firstName,
      ehrPatient.firstName,
      ehrPatient.first_name,
      plannerPatient.firstName,
      plannerPatient.first_name,
      source.first_name,
      source.firstName,
      fallbackParts.firstName,
    ),
    middleName: firstPresentValue(
      localPatient.middle_name,
      localPatient.middleName,
      ehrPatient.middleName,
      ehrPatient.middle_name,
      plannerPatient.middleName,
      plannerPatient.middle_name,
      source.middle_name,
      source.middleName,
      fallbackParts.middleName,
    ),
    lastName: firstPresentValue(
      localPatient.last_name,
      localPatient.lastName,
      ehrPatient.lastName,
      ehrPatient.last_name,
      plannerPatient.lastName,
      plannerPatient.last_name,
      source.last_name,
      source.lastName,
      fallbackParts.lastName,
    ),
    patientName: fallbackName,
  });
}
